import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { PexIcon } from './icons/PexIcon'
import { useT } from '../i18n'
import { orgContextI18n } from '../i18n/shared/org-context'
import { useOrgContextStore } from '../store/orgContext'
import { postOrgContextEvent } from '../lib/orgContextAudit'
import { usePricesHidden } from '../lib/price-mask'

// Persistent context indicator shown across the seller/supply shells while a
// platform admin is viewing another org. Owns the single exit affordance and
// emits the audit "exit" event. Read-only is enforced centrally in MSW.

type PlatformViewBannerProps = {
  /** Kapı kapalı (şalter kapalı ya da ekran beyan etmemiş): sebebini söyle.
   *  Boş bir ekran bırakmak kullanıcıya "bozuk" hissettiriyor; band neden
   *  boş olduğunu söylerse çıkış da anlamlı hale gelir (spec §5.3b). */
  viewAsDenied?: boolean
}

export function PlatformViewBanner({ viewAsDenied = false }: PlatformViewBannerProps = {}) {
  const t = useT(orgContextI18n)
  const navigate = useNavigate()
  const { mode, viewing, exitOrg } = useOrgContextStore()
  const queryClient = useQueryClient()
  const pricesHidden = usePricesHidden()

  if (mode !== 'platform_viewing' || !viewing) return null

  const exit = () => {
    postOrgContextEvent({
      type: 'exit',
      side: viewing.side,
      orgId: viewing.orgId,
      orgName: viewing.orgName,
      orgType: viewing.orgType,
      enteredWith: 'both',
    })
    // Leaving is the same context change as entering: the customer's cached
    // answers must not follow the admin back to their own panel.
    queryClient.clear()
    exitOrg()
    navigate({ to: '/admin' })
  }

  const sideLabel = t.banner.sideLabels[viewing.side]

  return (
    <div className="flex items-center gap-2 sm:gap-3 border-b border-amber-300 bg-amber-50 px-3 sm:px-6 py-2 text-xs sm:text-sm text-amber-900 safe-left safe-right">
      <PexIcon name="securityAlert" className="text-amber-600" size="xs" tone="inherit" />
      <span className="min-w-0 truncate">
        <strong className="hidden sm:inline">Platform Admin</strong>{' '}
        <span className="hidden sm:inline">{t.banner.viewingPrefix} {sideLabel}: </span>
        <strong>{viewing.orgName}</strong>
        <span className="ml-1 text-amber-700">({viewing.orgType})</span>
      </span>
      <span className="ml-1 hidden items-center gap-1 rounded bg-amber-200/70 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 sm:flex">
        <PexIcon name="preview" className="h-[11px] w-[11px]" size="xs" tone="inherit" />
        {t.banner.readOnly}
      </span>
      {pricesHidden && viewing.side === 'seller' && (
        <span
          className="ml-1 min-w-0 shrink rounded bg-amber-200/70 px-1.5 py-0.5 text-[11px] font-medium text-amber-900"
          role="status"
        >
          {t.banner.pricesHidden}
        </span>
      )}
      {viewAsDenied && (
        <span
          className="ml-1 min-w-0 shrink rounded bg-amber-200/70 px-1.5 py-0.5 text-[11px] font-medium text-amber-900"
          role="status"
        >
          {t.banner.notOpenForViewing}
        </span>
      )}
      <button
        type="button"
        onClick={exit}
        className="ml-auto flex shrink-0 items-center gap-1.5 rounded-md bg-amber-600 px-2.5 sm:px-3 py-1.5 sm:py-1 text-xs font-medium text-white hover:bg-amber-700 min-h-[40px] sm:min-h-0"
        aria-label={t.banner.backToMainPanel}
      >
        <PexIcon name="signOut" className="h-[13px] w-[13px]" size="xs" tone="inherit" />
        <span className="hidden sm:inline">{t.banner.backToMainPanel}</span>
      </button>
    </div>
  )
}
